import React from "react";
import { Loader2, Download, Mail, Trash2, Gift, Printer, CalendarCheck } from "lucide-react";
import api from "../../lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const STATUS_META = {
  pending: { label: "En attente paiement", cls: "bg-amber-500/15 text-amber-200 border-amber-400/30" },
  paid: { label: "Payée", cls: "bg-emerald-500/15 text-emerald-200 border-emerald-400/30" },
  redeemed: { label: "Utilisée", cls: "bg-accent-deep/15 text-accent-light border-accent-deep/30" },
  cancelled: { label: "Annulée", cls: "bg-white/5 text-text-cream/40 border-white/10" },
  expired: { label: "Expirée", cls: "bg-rose-500/15 text-rose-300 border-rose-400/30" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("fr-CH", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

export default function AdminGifts() {
  const [items, setItems] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState("");

  const load = React.useCallback(() => {
    setLoading(true);
    api.get("/admin/gifts")
      .then(r => setItems(r.data))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(() => { load(); }, [load]);

  const downloadPDF = (g) => {
    const token = localStorage.getItem("se_admin_token");
    const url = `${BACKEND}/api/admin/gifts/${g.id}/pdf`;
    // Fetch with auth, blob, then open
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.blob() : Promise.reject())
      .then(blob => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = `CarteCadeau_${g.code}.pdf`;
        a.click();
      })
      .catch(() => alert("Erreur téléchargement"));
  };
  const resend = async (g) => {
    setBusyId(g.id);
    try { await api.post(`/admin/gifts/${g.id}/resend`); alert("Email renvoyé"); load(); }
    catch { alert("Erreur"); } finally { setBusyId(""); }
  };
  const remove = async (g) => {
    if (!confirm(`Supprimer la carte cadeau ${g.code || g.id} ?`)) return;
    try { await api.delete(`/admin/gifts/${g.id}`); load(); } catch { alert("Erreur"); }
  };

  const stats = React.useMemo(() => {
    const s = { total: items.length, paid: 0, redeemed: 0, revenue: 0 };
    for (const g of items) {
      if (g.status === "paid") { s.paid++; s.revenue += g.amount_chf || 0; }
      if (g.status === "redeemed") { s.redeemed++; s.revenue += g.amount_chf || 0; }
    }
    return s;
  }, [items]);

  return (
    <div className="text-text-cream" data-testid="admin-gifts">
      <div className="mb-8">
        <div className="eyebrow mb-2">Admin</div>
        <h1 className="font-serif text-4xl">Cartes cadeau</h1>
        <p className="text-text-cream/50 mt-2 text-sm font-light max-w-2xl">
          {stats.total} au total · {stats.paid} actives · {stats.redeemed} utilisées · CA cumulé : <strong className="text-accent-light">CHF {stats.revenue}</strong>
        </p>
      </div>

      {loading && <div className="text-text-cream/60 text-sm inline-flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Chargement…</div>}
      {!loading && items.length === 0 && <div className="text-text-cream/50 italic">Aucune carte cadeau pour le moment.</div>}

      <div className="space-y-3">
        {items.map(g => {
          const meta = STATUS_META[g.status] || STATUS_META.pending;
          const busy = busyId === g.id;
          const print = !g.recipient_email;
          return (
            <div key={g.id} className="bg-white/[0.03] border border-white/10 rounded p-5 flex gap-4 items-start" data-testid={`gift-row-${g.id}`}>
              <Gift size={20} className="text-accent-light shrink-0 mt-1" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-serif text-base">{g.service_name}</span>
                  <span className="font-serif text-accent-light">CHF {g.amount_chf}</span>
                  <span className={`text-[0.6rem] uppercase tracking-wider border px-2 py-0.5 rounded ${meta.cls}`}>{meta.label}</span>
                  {print && g.status === "paid" && (
                    <span className="inline-flex items-center gap-1 text-[0.6rem] uppercase tracking-wider text-text-cream/50 border border-white/10 px-2 py-0.5 rounded">
                      <Printer size={9} /> À imprimer
                    </span>
                  )}
                </div>
                {g.code && (
                  <div className="font-mono text-sm text-accent-light tracking-widest mb-1">{g.code}</div>
                )}
                <div className="text-xs text-text-cream/60 font-light">
                  <strong>{g.buyer_prenom} {g.buyer_nom}</strong> ({g.buyer_email})
                  → {g.recipient_prenom || "destinataire"} {g.recipient_nom || ""}
                  {g.recipient_email && <> · {g.recipient_email}</>}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[0.65rem] text-text-cream/40 font-light flex-wrap">
                  <span>Achetée {fmtDate(g.created_at)}</span>
                  {g.expires_at && <span>Expire {fmtDate(g.expires_at)}</span>}
                  {g.redeemed_at && <span className="text-accent-light">Utilisée {fmtDate(g.redeemed_at)}</span>}
                  {g.delivered_at && <span className="text-emerald-300">Envoyée {fmtDate(g.delivered_at)}</span>}
                </div>
                {g.message && (
                  <div className="text-xs text-text-cream/60 italic font-light mt-2 pt-2 border-t border-white/5">« {g.message} »</div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {g.status === "paid" && (
                  <>
                    <button onClick={() => downloadPDF(g)} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-accent-deep/40 bg-accent-deep/10 text-accent-light hover:bg-accent-deep/20 transition" data-testid={`gift-pdf-${g.id}`}>
                      <Download size={12} /> PDF
                    </button>
                    <button onClick={() => resend(g)} disabled={busy} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded border border-white/15 text-text-cream hover:bg-white/5 transition disabled:opacity-50" data-testid={`gift-resend-${g.id}`}>
                      {busy ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />} Renvoyer
                    </button>
                  </>
                )}
                <button onClick={() => remove(g)} className="inline-flex items-center p-2 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 hover:bg-rose-500/20 transition" data-testid={`gift-delete-${g.id}`}>
                  <Trash2 size={12} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
